import type { TypedClient } from '@platform/openapi';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { usePageContext } from '../usePageContext.ts';
import styles from './Notifications.module.css';
import { NotificationsPanel } from './NotificationsPanel.tsx';
import { useInboxCounts } from './useNotifications.ts';

const BADGE_LIMIT = 99;

const badgeOf = (count: number): string => (count > BADGE_LIMIT ? `${String(BADGE_LIMIT)}+` : String(count));

export type NotificationBellProps = { api: TypedClient; className: string | undefined };

export const NotificationBell = ({ api, className }: NotificationBellProps) => {
  const { t } = usePageContext();
  const [open, setOpen] = useState(false);
  const counts = useInboxCounts(api);
  const unseen = counts.unseen;
  return (
    <>
      <button
        type="button"
        className={[className, styles['bell']].join(' ')}
        aria-label={unseen > 0 ? t('notifications.bellUnseen', { count: unseen }) : t('notifications.bell')}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Bell size={20} aria-hidden="true" />
        {unseen > 0 ? (
          <span className={styles['badge']} aria-hidden="true">
            {badgeOf(unseen)}
          </span>
        ) : null}
      </button>
      <span className="visually-hidden" aria-live="polite">
        {unseen > 0 ? t('notifications.announce', { count: unseen }) : ''}
      </span>
      {open ? (
        <NotificationsPanel
          api={api}
          counts={counts}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
